import React, { useEffect, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { api, downloadJson } from './api.js';
import { CurrentResults } from './CurrentResults.jsx';
import { assertReviewDetail, reviewPath, reviewStatus } from './current-results.js';
import { assertLivePage } from '../lib/live-epoch.js';

function ReadState({ state }) {
  if (state.error) return <div className="error-panel" role="alert">{state.error.message}</div>;
  if (!state.data) return <div className="loading-panel" role="status">Loading live Rosetta results…</div>;
  return null;
}

export function CurrentDashboard() {
  const [state, setState] = useState({});
  useEffect(() => {
    let active = true;
    api('/api/review/laws?limit=12').then(assertLivePage)
      .then(data => active && setState({ data })).catch(error => active && setState({ error }));
    return () => { active = false; };
  }, []);
  const data = state.data;
  const needsReview = Number(data?.counts?.failed || 0) + Number(data?.counts?.held || 0);
  return <>
    <header className="page-header live-page-header">
      <div>
        <div className="eyebrow">ROSETTA / LIVE</div>
        <h1>Current law decompositions.</h1>
        <p>Current law results are read from the selected runtime for the exact source identity. Missing selected-runtime results remain explicit; historical engines are never substituted.</p>
      </div>
      <div className="page-actions">
        <NavLink className="button button-primary" to="/intake">Process a Law</NavLink>
        <NavLink className="button" to="/layers">Inspect Layers</NavLink>
      </div>
    </header>
    <ReadState state={state} />
    {data && <>
      <section className="live-overview panel">
        <div className="live-overview-primary">
          <span className="eyebrow">CURRENT OPERATIONAL RECORD</span>
          <strong>{data.source_total?.toLocaleString() ?? '0'}</strong>
          <p>{data.source_total
            ? 'Source revisions represented only by their rule-line v9 state.'
            : 'No current rule-line source revisions are available.'}</p>
        </div>
        <div className="live-overview-stats">
          <div><span>Passed</span><strong>{data.counts.passed?.toLocaleString() ?? '0'}</strong></div>
          <div><span>Held</span><strong>{data.counts.held?.toLocaleString() ?? '0'}</strong></div>
          <div><span>Not run yet</span><strong>{data.counts.unprocessed?.toLocaleString() ?? '0'}</strong></div>
        </div>
      </section>
      {needsReview > 0 ? <p className="live-review-note">
        {needsReview.toLocaleString()} live result{needsReview === 1 ? '' : 's'} require review. <NavLink to="/pipeline">Open live pipeline →</NavLink>
      </p> : null}
      <section className="panel live-results-panel">
        <div className="section-heading">
          <div><span className="eyebrow">CURRENT RESULTS</span><h2>Live decompositions</h2></div>
          {data.source_total > 0 ? <NavLink className="text-button" to="/pipeline">Open live pipeline →</NavLink> : null}
        </div>
        {!data.laws.length ? <div className="live-empty-state">
          <strong>No current rule-line laws.</strong>
          <p>Historical campaigns and prior engineering runs remain outside the current execution surface.</p>
        </div> : <div className="run-grid">{data.laws.map(law => <article className="run-card live-run-card" key={law.source_registry_id}>
          <div className="run-card-topline">
            <span className="mono">{law.jurisdiction_code || 'Jurisdiction not recorded'}</span>
            <span className={`badge badge-${reviewStatus(law.status).tone}`}>{reviewStatus(law.status).label}</span>
          </div>
          <h3>{law.document_name || law.document_identifier}</h3>
          {law.document_identifier ? <p>{law.document_identifier}</p> : null}
          <NavLink className="button result-open" to={reviewPath(law.source_registry_id, law.attempt_id)}>Inspect law →</NavLink>
        </article>)}</div>}
        <small className="live-epoch-note">selected runtime only · no historical substitution · exact source spans revalidated on read.</small>
      </section>
    </>}
  </>;
}

const layers = [['sources', 'Sources'], ['blocks', 'Structure'], ['actors', 'Actors'], ['help', 'Help'], ['workflow', 'Workflow'], ['temporal', 'Deadlines'], ['accountability', 'Accountability'],
  ['override', 'Overrides'], ['definition', 'Definitions'], ['rule', 'Rules'], ['coverage', 'Coverage + Validation']];

export function CurrentLayers() {
  const [source, setSource] = useState(null);
  const [layer, setLayer] = useState('sources');
  const [state, setState] = useState({});
  useEffect(() => {
    if (!source) return;
    let active = true;
    setState({});
    const query = source.attempt_id ? `?attempt_id=${encodeURIComponent(source.attempt_id)}` : '';
    api(`/api/review/laws/${encodeURIComponent(source.source_registry_id)}${query}`)
      .then(data => assertReviewDetail(data, source.source_registry_id, source.attempt_id))
      .then(data => active && setState({ data })).catch(error => active && setState({ error }));
    return () => { active = false; };
  }, [source]);
  const data = state.data;
  const allObjects = data?.law_view?.objects || [];
  const objects = allObjects.filter(object => String(object.layer).toLowerCase() === layer);
  const selectedFields = allObjects.flatMap(object => collectFields(object.normalized_value, layer).map(field => ({ ...field, block: object.source_block_id })));
  const blockIds = [...new Set(allObjects.map(object => object.source_block_id).filter(value => value != null))];
  return <>
    <header className="page-header"><div><div className="eyebrow">LAYER VIEWER</div><h1>Rosetta Operating Structure</h1>
      <p>Select a source revision to inspect its stored six-layer rule-line decomposition and exact source evidence.</p></div>
      {data && <button className="button" onClick={() => downloadJson(`rosetta-layers-${source.source_registry_id}.json`, data)}>Export View</button>}</header>
    <div className="layer-layout"><nav className="layer-nav panel">{layers.map(([key, label]) =>
      <button key={key} className={layer === key ? 'active' : ''} onClick={() => setLayer(key)}>{label}</button>)}</nav>
      <section className="panel layer-content"><h2>{layers.find(([key]) => key === layer)?.[1]}</h2>
        {!source ? <p>Choose a law below.</p> : <><h3>{source.document_name}</h3><ReadState state={state} /></>}
        {data && (layer === 'sources' ? <dl><dt>Source</dt><dd>{data.source.document_identifier}</dd>
          <dt>Disposition</dt><dd>{reviewStatus(data.status).label}</dd><dt>Execution scope</dt><dd className="mono">rule_line_v9</dd><dt>Engine</dt><dd className="mono">rosetta-rule-ref-v9</dd><dt>Source hash</dt><dd className="mono">{data.source.source_content_hash}</dd></dl>
          : layer === 'coverage' ? <><div className="coverage-grid">{Object.entries(data.law_view?.coverage || {}).map(([key, value]) =>
            <article className="coverage-card" key={key}><strong>{key}</strong><p>{value.status}</p><p>{value.reason}</p></article>)}</div>
            <p>These five checks are recomputed against the stored text every time this detail is read.</p><ul>{(data.validation_results || []).map(row => <li key={row.test_name}>{row.test_name.replaceAll('_', ' ')}: {row.test_result}</li>)}</ul></>
          : layer === 'blocks' ? <><p>Structural units with no operative layer: {(data.law_view?.structural_representations || []).length}.</p>
            {(data.law_view?.structural_representations || []).length ? <div className="object-list">{data.law_view.structural_representations.map(unit => <article className="object-card" key={unit.unit_ord}><h3>Unit {unit.unit_ord} · {unit.kind || 'structure'}</h3><dl><dt>Path</dt><dd>{unit.path || '—'}</dd><dt>Section</dt><dd>{unit.section_label || '—'}</dd><dt>Offsets</dt><dd className="mono">{unit.char_offset_start}–{unit.char_offset_end}</dd><dt>SHA-256</dt><dd className="mono">{unit.raw_text_hash}</dd></dl></article>)}</div> : <p>No unlayered structural units are attached to this result.</p>}</>
          : ['actors', 'temporal'].includes(layer) ? <><p>Recorded fields from this source’s structural objects.</p>{selectedFields.length ? <dl>{selectedFields.map((field, i) => <React.Fragment key={i}><dt>{field.name.replaceAll('_', ' ')} · block {field.block ?? 'not attached'}</dt><dd><ReadableValue value={field.value} /></dd></React.Fragment>)}</dl> : <p>No fields are attached for this source and layer.</p>}</>
          : objects.length ? <div className="object-list">{objects.map((object, i) => { const value = object.normalized_value || {}; const span = object.metadata?.source_span || {}; return <article className="object-card" key={i}>
            <h3>{String(object.source_object_type || layer).replaceAll('_', ' ')}</h3>
            {typeof value.source_text === 'string' ? <><strong>Exact source text</strong><pre>{value.source_text}</pre></> : null}
            <dl>{Object.entries(value).filter(([key]) => key !== 'source_text' && key !== 'pass_2_fields').map(([key, item]) =>
              <React.Fragment key={key}><dt>{key.replaceAll('_', ' ')}</dt><dd><ReadableValue value={item} /></dd></React.Fragment>)}
              <dt>Source span</dt><dd className="mono">{Number.isInteger(span.char_offset_start) ? `${span.char_offset_start}–${span.char_offset_end}` : '—'}</dd>
              <dt>Span SHA-256</dt><dd className="mono">{span.raw_text_hash || '—'}</dd></dl>
          </article>; })}</div> : <p>{data.law_view?.coverage?.[layer === 'override' ? 'overrides' : layer === 'definition' ? 'definitions' : layer === 'rule' ? 'rules' : layer]?.reason || 'No live objects are attached for this layer.'}</p>)}
      </section></div>
    <CurrentResults title="Choose a law" onSelect={setSource} />
  </>;
}

export function RetiredLink() {
  return <section className="panel"><h1>Open the current result</h1>
    <p>This saved link is outside the current workspace. Select the source in the live pipeline to inspect its current result.</p>
    <NavLink className="button button-primary" to="/pipeline">Open Pipeline</NavLink></section>;
}

function ReadableValue({ value }) {
  if (value == null) return 'Not recorded';
  if (Array.isArray(value)) return <ul>{value.map((item, index) => <li key={index}><ReadableValue value={item} /></li>)}</ul>;
  if (typeof value === 'object') return <dl>{Object.entries(value).map(([key, item]) => <React.Fragment key={key}><dt>{key.replaceAll('_', ' ')}</dt><dd><ReadableValue value={item} /></dd></React.Fragment>)}</dl>;
  return String(value);
}

function collectFields(value, layer, path = '') {
  if (!value || typeof value !== 'object') return [];
  const matches = layer === 'actors' ? /(^|_)(actor|actors|enforcement_actor|entity_name)$/ : layer === 'temporal' ? /deadline|temporal|effective|expiration|duration|interval/ : /a^/;
  return Object.entries(value).flatMap(([key, item]) => matches.test(key) && item != null && item !== ''
    ? [{ name: path + key, value: item }]
    : typeof item === 'object' ? collectFields(item, layer, path + key + ' / ') : []);
}
